import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { MenuItemState } from '~/components/KebabMenu';
import { useUser } from '~/modules/auth/hooks/useUser';
import type { LabelGroupListItem } from '~settings/api/lib/schemas';
import { ActionsMenu } from '~settings/components/ActionsMenu';
import { TruncateWithTooltip } from '~settings/components/TruncateWithTooltip';
import { SETTINGS_ROUTES } from '~settings/config/routes';

export const useLabelGroupsColumns = (
  onDelete: (id: string, name: string) => void,
) => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { hasPermission } = useUser();

  return useMemo(() => {
    const columnHelper = createColumnHelper<LabelGroupListItem>();

    const userCanEdit = hasPermission('LABEL_GROUP.EDIT');
    const userCanDelete = hasPermission('LABEL_GROUP.DELETE');

    const labelGroupColumns = {
      name: columnHelper.accessor('name', {
        id: 'name',
        cell: (cell) => (
          <TruncateWithTooltip className="inline-block">
            {cell.getValue()}
          </TruncateWithTooltip>
        ),
        header: () => t('name'),
        enableSorting: true,
        size: 350,
      }),
      createdBy: columnHelper.accessor('createdByName', {
        id: 'createdBy',
        cell: (cell) => cell.getValue(),
        header: () => t('createdBy'),
        enableSorting: false,
        size: 200,
      }),
      numberOfLabels: columnHelper.accessor('numberOfLabels', {
        id: 'labels',
        cell: (cell) => {
          return cell.getValue() ? cell.getValue() : '-';
        },
        header: () => t('labels'),
        enableSorting: false,
        size: 100,
      }),
      numberOfUsers: columnHelper.accessor('numberOfUsers', {
        id: 'users',
        cell: (cell) => {
          return cell.getValue() ? cell.getValue() : '-';
        },
        header: () => t('users'),
        enableSorting: false,
        size: 100,
      }),
      controls: columnHelper.display({
        id: 'actions',
        cell: ({ row }) => {
          const { id, name } = row.original;

          const actions = [];

          if (userCanEdit) {
            actions.push({
              id: 'edit',
              label: t('labelGroup.editLink'),
              callback: () => navigate(SETTINGS_ROUTES.LABEL_GROUP_EDIT(id)),
            });
          }

          if (userCanDelete) {
            actions.push({
              id: 'delete',
              label: t('labelGroup.deleteAction'),
              state: 'error' as MenuItemState,
              callback: () => {
                onDelete(id, name);
              },
            });
          }

          return <ActionsMenu actions={actions} />;
        },
        size: 100,
      }),
    };

    const columns: (keyof typeof labelGroupColumns)[] = [
      'name',
      'createdBy',
      'numberOfLabels',
      'numberOfUsers',
    ];

    if (userCanEdit || userCanDelete) {
      columns.push('controls');
    }

    return columns.map((key) => labelGroupColumns[key]);
  }, [hasPermission, navigate, onDelete, t]);
};
